import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import useSEO from '../hooks/useSEO';
import { FaCheckCircle, FaMapPin, FaMousePointer, FaListUl, FaRocket, FaChrome } from 'react-icons/fa';
import { Download, PlayCircle, Star } from 'lucide-react';

function ThankYou() {
  const [isVisible, setIsVisible] = useState(false);

  // Apply SEO metadata
  useSEO({
    title: "Thank You for Installing GLabs Automator! 🎉",
    description: "Get started with GLabs Automator in minutes. Follow these simple steps to automate your Google Labs AI content generation.",
    keywords: "GLabs Automator setup, installation guide, getting started",
    openGraph: {
      type: "website"
    }
  });

  useEffect(() => {
    setIsVisible(true);
    // Confetti effect on page load
    if (window.innerWidth > 768) {
      createConfetti();
    }
  }, []);

  const createConfetti = () => {
    const colors = ['#4285F4', '#EA4335', '#FBBC04', '#34A853'];
    const confettiCount = 50;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      confetti.style.left = Math.random() * 100 + '%';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.animationDelay = Math.random() * 3 + 's';
      confetti.style.animationDuration = Math.random() * 3 + 2 + 's';
      document.getElementById('confetti-container')?.appendChild(confetti);
      
      setTimeout(() => confetti.remove(), 5000);
    }
  };

  const steps = [
    {
      icon: <FaMapPin className="text-4xl text-white" />,
      title: "Pin the Extension",
      description: "Click the puzzle icon (🧩) in your Chrome toolbar, then click the pin icon next to GLabs Automator to keep it visible and easily accessible.",
    },
    {
      icon: <FaMousePointer className="text-4xl text-white" />,
      title: "Open GLabs Automator",
      description: "Click on the GLabs Automator icon in your Chrome toolbar to open the extension panel and access all automation features.",
    },
    {
      icon: <FaListUl className="text-4xl text-white" />,
      title: "Add Your Prompts",
      description: "Enter or paste your list of prompts (one per line) in the extension interface. You can add as many as you need for bulk generation!",
    },
    {
      icon: <FaRocket className="text-4xl text-white" />,
      title: "Start Generating",
      description: "Navigate to your chosen Google Labs tool (ImageFX, Whisk, MusicFX, or Flow) and click 'Start Automation'. Watch the magic happen!",
    }
  ];

  const tools = [
    { name: "ImageFX", url: "https://aitestkitchen.withgoogle.com/tools/image-fx", emoji: "🎨" },
    { name: "Whisk", url: "https://labs.google/fx/tools/whisk", emoji: "🖼️" },
    { name: "MusicFX", url: "https://aitestkitchen.withgoogle.com/tools/music-fx", emoji: "🎵" },
    { name: "Flow (VEO3)", url: "https://labs.google/fx/tools/video-fx", emoji: "🎬" }
  ];

  return (
    <div className="bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Custom Cursor */}
      <CustomCursor />

      {/* Navigation */}
      <Navigation />

      {/* Confetti Container */}
      <div id="confetti-container" className="fixed inset-0 pointer-events-none z-50 overflow-hidden"></div>

      {/* Hero Section */}
      <section className="bg-gray-50 dark:bg-gray-900 py-20 relative overflow-hidden">
        {/* Google-themed ambient backgrounds */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#FBBC04]"></div>
          <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-1/4 left-1/3 w-72 h-72 rounded-full blur-3xl animate-pulse bg-[#4285F4]" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="flex justify-center mb-6">
              <div className="relative">
                <FaCheckCircle className="text-8xl text-[#34A853] drop-shadow-2xl animate-bounce" />
                <div className="absolute inset-0 animate-ping">
                  <FaCheckCircle className="text-8xl text-[#34A853] opacity-20" />
                </div>
              </div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-4 animate-fadeInUp">
              Thank You for Installing! 🎉
            </h1>
            
            <p className="text-2xl lg:text-3xl text-gray-700 dark:text-gray-200 mb-6 font-semibold animate-fadeInUp" style={{ animationDelay: '200ms' }}>
              You're all set to automate your AI content creation!
            </p>
            
            <p className="text-lg lg:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-8 animate-fadeInUp" style={{ animationDelay: '400ms' }}>
              GLabs Automator is now installed in your Chrome browser. Follow the simple steps below to get started and create hundreds of AI-generated images, music, and videos in minutes!
            </p>

            <div className="flex flex-wrap gap-4 justify-center animate-fadeInUp" style={{ animationDelay: '600ms' }}>
              <Link to="/">
                <button className="px-8 py-4 glass-card text-gray-900 dark:text-white font-bold rounded-2xl hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center gap-2 ripple-effect">
                  Back to Home
                </button>
              </Link>
              <a href="https://youtu.be/Do85QlO1UOU?si=i9jYVcBS2PCtgkLM" target="_blank" rel="noreferrer">
                <button className="px-8 py-4 bg-gradient-to-r from-[#34A853] to-[#0f9d58] dark:from-[#8B5CF6] dark:to-[#7C3AED] text-white font-bold rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center gap-2 ripple-effect group">
                  <PlayCircle className="h-5 w-5 group-hover:scale-110 transition-transform" />
                  Watch Tutorial
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Steps */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Getting Started is <span className="text-[#4285F4]">Easy</span>! 🚀
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto">
              Follow these 4 simple steps to start automating your Google Labs content generation
            </p>
          </div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`flex flex-col lg:flex-row items-center gap-8 transform transition-all duration-700 ${
                  isVisible ? 'translate-x-0 opacity-100' : index % 2 === 0 ? '-translate-x-10 opacity-0' : 'translate-x-10 opacity-0'
                }`}
              >
                {/* Step Number & Icon */}
                <div className={`flex-shrink-0 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="relative">
                    <div className={`w-32 h-32 rounded-3xl flex items-center justify-center shadow-2xl transform hover:rotate-6 transition-transform duration-300 ${
                      ['bg-[#4285F4]', 'bg-[#EA4335]', 'bg-[#FBBC04]', 'bg-[#34A853]'][index]
                    }`}>
                      {step.icon}
                    </div>
                    <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-gray-900 to-gray-700 dark:from-white dark:to-gray-200 text-white dark:text-gray-900 rounded-full flex items-center justify-center font-bold text-xl shadow-xl">
                      {index + 1}
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className={`flex-1 glass-card rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{step.title}</h3>
                  <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Labs Tools Section */}
      <section className="py-20 bg-white dark:bg-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#34A853]" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Choose Your <span className="text-[#EA4335]">Tool</span> 🎯
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Click on any Google Labs tool to get started with automation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((tool, index) => (
              <a
                key={index}
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center group ripple-effect animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {tool.emoji}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{tool.name}</h3>
                <div className="flex items-center justify-center gap-2 font-bold text-[#4285F4]">
                  <span>Get Started</span>
                  <FaRocket className="animate-bounce" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Pro Tips Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fadeInUp">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Pro <span className="text-[#FBBC04]">Tips</span> 💡
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Get the most out of GLabs Automator
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { emoji: "⚡", title: "Batch Processing", desc: "Add multiple prompts at once (one per line) to generate batches of content automatically." },
              { emoji: "💾", title: "Auto-Download", desc: "Enable auto-download to save all generated content directly to your computer without manual clicking." },
              { emoji: "⏱️", title: "Set Custom Delays", desc: "Adjust the timing between generations to match your workflow and avoid overwhelming your browser." },
              { emoji: "📋", title: "CSV Export", desc: "Export your prompts and results to CSV format for easy organization and record-keeping." },
              { emoji: "🎨", title: "Multi-Tool Support", desc: "Switch between ImageFX, Whisk, MusicFX, and Flow seamlessly with one extension." },
              { emoji: "🔄", title: "Resume Anytime", desc: "If automation stops unexpectedly, simply refresh the page and restart from where you left off." }
            ].map((tip, index) => (
              <div
                key={index}
                className="glass-card p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ripple-effect animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl mb-4">{tip.emoji}</div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{tip.title}</h3>
                <p className="text-gray-700 dark:text-gray-300">{tip.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 animate-fadeInUp">
            Need Help? We've Got You! 🤝
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            If you run into any issues or have questions, we're here to help you succeed.
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center animate-fadeInUp" style={{ animationDelay: '400ms' }}>
            <Link to="/#faq">
              <button className="px-8 py-4 bg-[#4285F4] hover:bg-[#357ae8] text-white font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl ripple-effect">
                View FAQ
              </button>
            </Link>
            
            <a href="https://intercom.help/connectautoplaylabs/en" target="_blank" rel="noreferrer">
              <button className="px-8 py-4 glass-card border-2 border-[#4285F4] text-white font-bold rounded-2xl hover:bg-[#4285F4] transition-all duration-300 transform hover:scale-105 shadow-xl ripple-effect">
                Contact Support
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white dark:bg-gray-900 relative overflow-hidden">
        {/* Google-themed ambient backgrounds */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#FBBC04]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/3 right-1/3 w-72 h-72 rounded-full blur-3xl animate-pulse bg-[#4285F4]" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 animate-fadeInUp">
            Ready to Create Amazing <span className="text-[#4285F4]">AI Content</span>? ✨
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto animate-fadeInUp" style={{ animationDelay: '200ms' }}>
            Choose your preferred Google Labs tool and start your automated content generation journey now!
          </p>
          
          <div className="flex flex-wrap gap-4 justify-center animate-fadeInUp" style={{ animationDelay: '400ms' }}>
            {tools.map((tool, index) => (
              <a key={index} href={tool.url} target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 bg-gradient-to-r from-[#34A853] to-[#0f9d58] dark:from-[#8B5CF6] dark:to-[#7C3AED] text-white font-bold text-lg rounded-2xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 shadow-xl flex items-center gap-3 ripple-effect group">
                  <span>{tool.emoji}</span>
                  <span>{tool.name}</span>
                  <FaRocket className="group-hover:scale-110 transition-transform" />
                </button>
              </a>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Confetti CSS */}
      <style>{`
        .confetti {
          position: fixed;
          width: 10px;
          height: 10px;
          top: -10px;
          z-index: 9999;
          animation: confetti-fall linear forwards;
        }

        @keyframes confetti-fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

export default ThankYou;
